import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";

export default function AdminCredits() {
  const [trends, setTrends] = useState(null);
  const [topUsers, setTopUsers] = useState([]);
  const [planBreakdown, setPlanBreakdown] = useState(null);
  const [costMetrics, setCostMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    fetchAnalytics();
  }, [days]);

  const fetchAnalytics = async () => {
    const token = localStorage.getItem("admin_token");
    setLoading(true);
    try {
      const [trendsRes, topUsersRes, planRes, costRes] = await Promise.all([
        axios.get(`${API}/admin/credits/trends?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/credits/top-users?days=${days}&limit=10`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/credits/by-plan?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/admin/credits/cost-metrics?days=${days}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setTrends(trendsRes.data);
      setTopUsers(topUsersRes.data.top_users);
      setPlanBreakdown(planRes.data.breakdown);
      setCostMetrics(costRes.data);
    } catch (error) {
      toast.error("Failed to load analytics");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading analytics...</p>
        </div>
      </div>
    );
  }

  const getPlanColor = (plan) => {
    const colors = {
      free: "from-slate-500 to-slate-600",
      starter: "from-blue-500 to-blue-600",
      pro: "from-purple-500 to-purple-600",
      org: "from-teal-500 to-teal-600",
    };
    return colors[plan] || "from-slate-500 to-slate-600";
  };

  const maxCredits = Math.max(...(trends?.timeline || []).map(t => t.credits_used), 1);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Credits & Usage</h1>
          <p className="text-muted-foreground mt-1">Analytics and consumption patterns</p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(parseInt(e.target.value))}
          className="px-4 py-2 bg-muted border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        >
          <option value="7">Last 7 days</option>
          <option value="30">Last 30 days</option>
          <option value="60">Last 60 days</option>
          <option value="90">Last 90 days</option>
        </select>
      </div>

      {/* Cost Metrics KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-muted-foreground text-sm">Total Credits Used</p>
            <span className="text-2xl">🎟️</span>
          </div>
          <p className="text-3xl font-bold text-foreground">{costMetrics?.total_credits_consumed || 0}</p>
          <p className="text-muted-foreground text-xs mt-1">Last {days} days</p>
        </div>

        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-muted-foreground text-sm">Avg Per User</p>
            <span className="text-2xl">👤</span>
          </div>
          <p className="text-3xl font-bold text-foreground">{costMetrics?.credits_per_paying_user || 0}</p>
          <p className="text-muted-foreground text-xs mt-1">Credits per paying user</p>
        </div>

        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-muted-foreground text-sm">Revenue per Credit</p>
            <span className="text-2xl">💰</span>
          </div>
          <p className="text-3xl font-bold text-foreground">${costMetrics?.revenue_per_credit?.toFixed(4) || 0}</p>
          <p className="text-muted-foreground text-xs mt-1">Revenue efficiency</p>
        </div>

        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-muted-foreground text-sm">Exhaustion Events</p>
            <span className="text-2xl">⚠️</span>
          </div>
          <p className="text-3xl font-bold text-foreground">{costMetrics?.credit_exhaustion_events || 0}</p>
          <p className="text-muted-foreground text-xs mt-1">Users hit 0 credits</p>
        </div>
      </div>

      {/* Credit Consumption Trend Chart */}
      <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
        <h2 className="text-xl font-bold text-foreground mb-6">Credit Consumption Trend</h2>
        <div className="space-y-2">
          <div className="flex items-end justify-between gap-1 h-64">
            {trends?.timeline?.map((day, idx) => {
              const height = (day.credits_used / maxCredits) * 100;
              return (
                <div key={idx} className="flex-1 flex flex-col items-center justify-end group">
                  <div className="relative w-full">
                    <div
                      className="w-full bg-gradient-to-t from-teal-500 to-teal-400 rounded-t transition-all hover:from-teal-400 hover:to-teal-300"
                      style={{ height: `${Math.max(height, 2)}%` }}
                    />
                    <div className="absolute -top-8 left-1/2 -translate-x-1/2 hidden group-hover:block bg-muted px-2 py-1 rounded text-xs text-foreground whitespace-nowrap">
                      {day.credits_used} credits
                    </div>
                  </div>
                  {idx % Math.ceil(trends.timeline.length / 10) === 0 && (
                    <p className="text-xs text-muted-foreground mt-2 rotate-45 origin-left">
                      {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex items-center justify-between text-sm text-muted-foreground pt-4">
            <span>Total: {trends?.total_credits_used || 0} credits</span>
            <span>Daily Avg: {trends?.avg_daily_usage || 0} credits</span>
          </div>
        </div>
      </div>

      {/* Top Users & Plan Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Users */}
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-6">Top Users by Usage</h2>
          <div className="space-y-3">
            {topUsers.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No usage data</p>
            ) : (
              topUsers.map((user, idx) => (
                <div key={idx} className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-gradient-to-br from-teal-500 to-teal-600 rounded-full flex items-center justify-center text-foreground font-bold">
                      #{idx + 1}
                    </div>
                    <div>
                      <p className="text-foreground font-medium">{user.name}</p>
                      <p className="text-muted-foreground text-xs">{user.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-foreground font-bold">{user.credits_used}</p>
                    <p className="text-muted-foreground text-xs capitalize">{user.plan_type}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Plan Breakdown */}
        <div className="bg-card backdrop-blur-sm border border-border rounded-xl p-6">
          <h2 className="text-xl font-bold text-foreground mb-6">Usage by Plan</h2>
          <div className="space-y-4">
            {planBreakdown && Object.entries(planBreakdown).map(([plan, data]) => (
              <div key={plan} className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 bg-gradient-to-br ${getPlanColor(plan)} rounded-full`}></div>
                    <span className="text-foreground font-medium capitalize">{plan}</span>
                  </div>
                  <span className="text-muted-foreground text-sm">{data.user_count} users</span>
                </div>
                <div className="grid grid-cols-2 gap-4 pl-5">
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <p className="text-muted-foreground text-xs">Total Credits</p>
                    <p className="text-foreground font-bold">{data.credits_used}</p>
                  </div>
                  <div className="bg-muted/50 p-3 rounded-lg">
                    <p className="text-muted-foreground text-xs">Avg per User</p>
                    <p className="text-foreground font-bold">{data.avg_per_user}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
